import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { format, isAfter, subDays } from 'date-fns';
import { AppView, Indicator, SettingsItem, SettingsItemNavigate, SCREEN_BOTTOM_PADDING, SettingsItemRightContent, withAnimatedNavigationHeader, } from 'design-system';
import { IconDownload } from 'design-system/icons';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import FlatListItemCard from 'design-system/FlatListItemCard';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { selectTradingAccount } from 'features/invest/reducers/selectors';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useGenerateStatements } from '../hooks/useGenerateStatements';
import { getDateInfo } from './utils';
import { isWeb } from '../../../constants';
const getInfo = (type) => {
    switch (type) {
        case 'CapitalGainsAndIncome':
            return {
                title: 'Download the trading activity statement',
                navigationHeaderTitle: 'Trading activity statement',
                desc: 'This statement is provided by Emma and documents your trading activity from the Emma invest product',
            };
        case 'Pots':
            return {
                title: 'Download the interest pots statement',
                navigationHeaderTitle: 'Interest pots statement',
                desc: 'This statement is provided by Emma and documents the transactions and interest earned in your interest bearing pots',
            };
        default:
            return undefined;
    }
};
const ExportStatement = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const styles = useStyles(styleSet);
    const tradingAccount = useAppSelector(selectTradingAccount);
    const investStartedTimeStamp = tradingAccount?.account.timestamp;
    const kycStartedAt = useAppSelector((state) => state.user.user.kycStartedAt);
    const { type } = route.params;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={type === 'Pots' ? HELP_ARTICLES_KEY.STATEMENT_POTS : undefined}/>),
        });
    }, [type]);
    const [downloadingTitle, setDownloadingTitle] = useState(undefined);
    const info = useMemo(() => getInfo(type), [type]);
    const { isFetching, generateStatements } = useGenerateStatements(type);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const choicesObj = useMemo(() => {
        if (type === 'CapitalGainsAndIncome') {
            const dateToUse = investStartedTimeStamp || kycStartedAt;
            if (dateToUse) {
                const data = getDateInfo(dateToUse);
                return {
                    data,
                    dataLength: data.length,
                };
            }
        }
        else if (kycStartedAt) {
            const data = getDateInfo(kycStartedAt);
            return {
                data,
                dataLength: data.length,
            };
        }
        return undefined;
    }, [investStartedTimeStamp, kycStartedAt, type]);
    const handleCustomPeriod = useCallback(() => {
        navigation.navigateDeprecated('SelectCustomPeriodForStatements', {
            type,
        });
    }, [type]);
    const handleGenerate = useCallback((from, to, title) => async () => {
        if (isFetching)
            return;
        setDownloadingTitle(title);
        const yesterday = subDays(new Date(), 1);
        await generateStatements(from, isAfter(to, yesterday) ? format(yesterday, 'yyyy-MM-dd') : to);
        setDownloadingTitle(undefined);
    }, [generateStatements, isFetching]);
    if (!info) {
        return <AppView withFrame={false}/>;
    }
    return (<AppView withFrame={false}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} keyboardShouldPersistTaps="handled" contentContainerStyle={contentContainerStyle}>
          <ScreenInfoHeader title={info.title} subTitle={info.desc}/>
          <TouchableBounceCard onDark noMarginTop onPress={handleCustomPeriod}>
            <View pointerEvents="none">
              <SettingsItemNavigate title="Custom period"/>
            </View>
          </TouchableBounceCard>
          {choicesObj?.data.map((choice, index) => (<FlatListItemCard key={choice.title} index={index} style={styles.flatListItemCard} itemsLength={choicesObj.data.length} onPress={handleGenerate(choice.from, choice.to, choice.title)}>
              <SettingsItem title={choice.title} description={choice.description}>
                <SettingsItemRightContent>
                  {isFetching && downloadingTitle === choice.title ? (<Indicator dark/>) : (<IconDownload alt colorKey="secondary"/>)}
                </SettingsItemRightContent>
              </SettingsItem>
            </FlatListItemCard>))}
        </Animated.ScrollView>
      </AppView>);
}, ({ colors, route }) => ({
    hideTitleOnScroll: true,
    title: getInfo(route.params.type)?.navigationHeaderTitle || '',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    flatListItemCard: {
        backgroundColor: colors.cards.onDark,
    },
}));
export default ExportStatement;
